/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * LivePreview — modal that loads arcee-client's /p/preview route in an iframe
 * and sends the current page schema + theme via postMessage.
 * Responsive preview modes: Desktop / Tablet / Mobile.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';

const PREVIEW_URL = process.env.REACT_APP_PUBLIC_CLIENT_URL
  ? `${process.env.REACT_APP_PUBLIC_CLIENT_URL}/p/preview`
  : null;

const WIDTHS = {
  desktop: '100%',
  tablet:  '768px',
  mobile:  '375px',
};

export default function LivePreview({ sections, onClose }) {
  const { theme } = useTheme();
  const iframeRef = useRef(null);
  const [mode, setMode] = useState('desktop');
  const [ready, setReady] = useState(false);

  const primary   = theme.primary      || '#4e2e80';
  const secondary = theme.secondary    || '#ff7a59';
  const gradient  = theme.gradientBackground || `linear-gradient(90deg, ${primary}, ${secondary})`;
  const btnRadius = theme.buttonRadius || '0.5rem';

  // When the iframe signals ready, or when sections/theme change, send schema
  const sendSchema = () => {
    if (!iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage(
      {
        type: 'ARCEE_PAGE_SCHEMA',
        schema: { sections },
        theme,
      },
      '*'
    );
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'ARCEE_PREVIEW_READY') {
        setReady(true);
        sendSchema();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Re-send whenever sections or theme change (debounced via the effect deps)
  useEffect(() => {
    if (ready) sendSchema();
  }, [ready, sections, theme]);

  if (!PREVIEW_URL) {
    return (
      <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Live Preview</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body text-center py-5">
              <i className="bi bi-exclamation-triangle text-warning" style={{ fontSize: 32 }} />
              <p className="mt-3 text-muted">
                <code>REACT_APP_PUBLIC_CLIENT_URL</code> is not configured.
                <br />Set it in <code>.env.local</code> to enable live preview.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ background: 'rgba(0,0,0,0.6)' }}
    >
      <div className="modal-dialog modal-fullscreen">
        <div className="modal-content" style={{ background: '#f1f5f9', display: 'flex', flexDirection: 'column' }}>
          {/* Toolbar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 16px',
              background: gradient,
              flexShrink: 0,
              boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
            }}
          >
            {/* Title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: 'rgba(255,255,255,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <i className="bi bi-eye" style={{ color: '#fff', fontSize: 13 }} />
              </div>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, lineHeight: 1.2 }}>
                  Live Preview
                </div>
                <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 10, lineHeight: 1.2 }}>
                  {mode === 'desktop' ? 'Full width' : WIDTHS[mode]}
                </div>
              </div>
            </div>

            {/* Responsive mode toggles */}
            <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
              {[
                { key: 'desktop', icon: 'bi-display',  label: 'Desktop' },
                { key: 'tablet',  icon: 'bi-tablet',   label: 'Tablet'  },
                { key: 'mobile',  icon: 'bi-phone',    label: 'Mobile'  },
              ].map(m => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMode(m.key)}
                  title={m.label}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '5px 12px',
                    border: 'none',
                    borderRadius: btnRadius,
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    transition: 'background 0.15s',
                    background: mode === m.key ? '#fff' : 'rgba(255,255,255,0.15)',
                    color:      mode === m.key ? primary : 'rgba(255,255,255,0.85)',
                    boxShadow:  mode === m.key ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                  }}
                >
                  <i className={`bi ${m.icon}`} />
                  <span className="d-none d-md-inline">{m.label}</span>
                </button>
              ))}
            </div>

            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              title="Close preview"
              style={{
                marginLeft: 'auto',
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 14px',
                background: '#fff',
                border: 'none',
                borderRadius: btnRadius,
                color: primary,
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <i className="bi bi-x-lg" />
              Close
            </button>
          </div>

          {/* iframe container */}
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              overflowY: 'auto',
              padding: mode === 'desktop' ? 0 : '24px 0',
              borderTop: `3px solid ${primary}`,
            }}
          >
            <div
              style={{
                width: WIDTHS[mode],
                maxWidth: '100%',
                height: '100%',
                minHeight: 600,
                background: '#fff',
                boxShadow: mode !== 'desktop' ? '0 4px 24px rgba(0,0,0,0.18)' : 'none',
                transition: 'width 0.3s ease',
              }}
            >
              {!ready && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: '#94a3b8', fontSize: 13 }}>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Loading preview…
                </div>
              )}
              <iframe
                ref={iframeRef}
                src={PREVIEW_URL}
                title="Page Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: 600,
                  border: 'none',
                  display: ready ? 'block' : 'none',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
